import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  WorkspacePremium as WorkspacePremiumIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getLocalizedField } from '../utils/courseDisplay';
import StudentAssessmentSection from '../components/StudentAssessmentSection';

interface StudentDetailData {
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    defaultLanguage?: string | null;
    team?: string | null;
    position?: string | null;
    disability?: boolean | null;
  };
  course: {
    id: string;
    courseProject?: string;
    title: { [key: string]: string };
    description: { [key: string]: string };
    defaultLanguage?: string;
    diagnosticImpactEnabled?: boolean;
  };
  enrollment: {
    enrollmentDate: string;
    completionDate: string | null;
    isActive: boolean;
    progress: number;
  };
  group?: { id: string; name: string } | null;
  assessment?: {
    enabled: boolean;
    measurements: any[];
    linkedEvolution: any[];
  };
  chapters: Array<{
    chapter: {
      id: string;
      title: { [key: string]: string };
      order: number;
      description: { [key: string]: string };
      deadline: string | null;
    };
      chapterRating?: null | {
        rating: number;
        comment?: string;
        createdAt: string;
        updatedAt: string;
      };
    progress: {
      isCompleted: boolean;
      completionDate: string | null;
      isUnlocked: boolean;
      timeSpent: number;
      completedItems: string[];
      completedModules: string[];
    };
    items: Array<{
      id: string;
      itemType: 'capsule' | 'quiz' | 'workshop' | string;
      order: number;
      title: { [key: string]: string };
      description?: { [key: string]: string };
      timeSpent?: number;
      duration?: number;
      date?: string;
      startTime?: string;
      endTime?: string;
      note?: null | {
        id: string;
        content: string;
        createdAt: string;
        updatedAt: string;
      };
      modules?: Array<{
        id: string;
        order: number;
        type: string;
        content: any;
        hasSubmission: boolean;
        submission: null | {
          answer: string;
          answerType: string;
          submissionDate: string;
          isGraded: boolean;
          grade?: number;
          feedback?: string;
        };
      }>;
      response?: null | {
        score: number;
        completedAt: string;
        answers: Array<{
          questionId?: string | null;
          questionOrder: number;
          questionText: { [key: string]: string } | null;
          options: Array<{ id?: string; [key: string]: string | undefined }>;
          selectedOptionId?: string | null;
          selectedAnswerIndex: number;
          selectedAnswerText: { [key: string]: string } | null;
          correctAnswerIndex?: number;
          correctAnswerText?: { [key: string]: string } | null;
        }>;
      };
      workshopRating?: null | {
        status: 'rated' | 'absent';
        rating?: number;
        comment?: string;
        updatedAt: string;
      };
    }>;
    quizResponses: Array<{
      id: string;
      score: number;
      totalQuestions: number;
      percentage: number;
      completedAt: string;
    }>;
  }>;
  statistics: {
    totalChapters: number;
    completedChapters: number;
    progressPercentage: number;
    totalTimeSpent: number;
    averageQuizScore: number;
  };
}

const StudentDetail: React.FC = () => {
  const { courseId, studentId } = useParams<{ courseId: string; studentId: string }>();

  const [data, setData] = useState<StudentDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [certEligible, setCertEligible] = useState(false);
  const [certCourseType, setCertCourseType] = useState<string | null>(null);
  const [downloadingCert, setDownloadingCert] = useState(false);

  useEffect(() => {
    if (courseId && studentId) {
      fetchStudentDetail();
      fetchCertificateEligibility();
    }
  }, [courseId, studentId]);

  const fetchCertificateEligibility = async () => {
    try {
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/certificates/course/${courseId}/student/${studentId}/eligibility`
      );
      if (!response.ok) {
        setCertEligible(false);
        return;
      }
      const result = await response.json();
      setCertEligible(!!result.eligible);
      setCertCourseType(result.courseType ?? null);
    } catch {
      setCertEligible(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      setDownloadingCert(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/certificates/course/${courseId}/student/${studentId}/download`
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Impossible de générer le certificat');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificat-${data?.student.lastName || 'apprenant'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Erreur réseau lors du téléchargement du certificat');
    } finally {
      setDownloadingCert(false);
    }
  };

  const fetchStudentDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/courses/${courseId}/students/${studentId}/detail`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Impossible de charger le détail de l'apprenant");
        return;
      }

      const result = await response.json();
      setData(result);
    } catch (e) {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value?: string | null): string => {
    if (!value) return '-';
    return new Date(value).toLocaleString('fr-FR');
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || seconds <= 0) return '0s';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return `${seconds}s`;
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}min`;
  };

  const courseLanguage = data?.course.defaultLanguage || 'en';

  const getLocalizedText = (text: any, fallback = ''): string =>
    getLocalizedField(text, courseLanguage, fallback);

  const getChapterDisplayTitle = (chapter: StudentDetailData['chapters'][number]['chapter']): string => {
    const label = getLocalizedText(chapter.description, '').trim();
    const title = getLocalizedText(chapter.title, 'Sans titre').trim();
    return label ? `${label} : ${title}` : title;
  };

  if (loading) {
    return <Typography color="text.secondary">Chargement du suivi…</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">Aucune donnée disponible</Alert>;
  }

  return (
    <Box>
      {certEligible && certCourseType !== 'coaching' && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            startIcon={<WorkspacePremiumIcon />}
            onClick={handleDownloadCertificate}
            disabled={downloadingCert}
          >
            {downloadingCert ? 'Génération…' : 'Télécharger le certificat'}
          </Button>
        </Box>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Progression globale
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <LinearProgress
                variant="determinate"
                value={data.statistics.progressPercentage}
                sx={{ flex: 1, height: 8, borderRadius: 1 }}
              />
              <Typography variant="h6" sx={{ minWidth: 48, textAlign: 'right' }}>
                {data.statistics.progressPercentage}%
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}><CardContent><Box display="flex" alignItems="center"><SchoolIcon sx={{ mr: 1 }} /><Box><Typography variant="h6">{data.statistics.completedChapters}/{data.statistics.totalChapters}</Typography><Typography variant="body2" color="text.secondary">Sections complétées</Typography></Box></Box></CardContent></Card>
        <Card sx={{ flex: 1 }}><CardContent><Box display="flex" alignItems="center"><ScheduleIcon sx={{ mr: 1 }} /><Box><Typography variant="h6">{formatTime(data.statistics.totalTimeSpent)}</Typography><Typography variant="body2" color="text.secondary">Temps total</Typography></Box></Box></CardContent></Card>
        <Card sx={{ flex: 1 }}><CardContent><Box display="flex" alignItems="center"><TrendingUpIcon sx={{ mr: 1 }} /><Box><Typography variant="h6">{data.statistics.averageQuizScore}%</Typography><Typography variant="body2" color="text.secondary">Score moyen quiz</Typography></Box></Box></CardContent></Card>
      </Stack>

      {data.assessment?.enabled && (
        <StudentAssessmentSection
          enabled={data.assessment.enabled}
          measurements={data.assessment.measurements || []}
          linkedEvolution={data.assessment.linkedEvolution || []}
          defaultLanguage={courseLanguage}
        />
      )}

      {data.chapters.map((chapterData) => (
        <Accordion key={chapterData.chapter.id} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">{getChapterDisplayTitle(chapterData.chapter)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Échéance : {formatDate(chapterData.chapter.deadline)}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Chip
                  label={chapterData.progress.isCompleted ? 'Terminé' : 'En cours'}
                  color={chapterData.progress.isCompleted ? 'success' : 'default'}
                  icon={chapterData.progress.isCompleted ? <CheckCircleIcon /> : undefined}
                />
                {chapterData.chapterRating && (
                  <Typography variant="body2" color="primary.main" mt={1}>
                    Note section : {chapterData.chapterRating.rating}/10
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Temps section : {formatTime(chapterData.progress.timeSpent)}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {getLocalizedText(chapterData.chapter.description)}
            </Typography>

            <Typography variant="subtitle1" fontWeight={600} mb={1}>Progression par item</Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Titre</TableCell>
                    <TableCell>Temps passé</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Note atelier</TableCell>
                    <TableCell>Commentaire atelier</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chapterData.items.map((item) => {
                    const itemId = item.id?.toString?.() || item.id;
                    const isCompleted = chapterData.progress.completedItems.some(
                      (x: any) => x?.itemId?.toString?.() === itemId
                    );
                    return (
                      <TableRow key={item.id}>
                        <TableCell>{item.itemType}</TableCell>
                        <TableCell>{getLocalizedText(item.title)}</TableCell>
                        <TableCell>{formatTime(item.timeSpent || 0)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={isCompleted ? 'Complété' : 'Non complété'} color={isCompleted ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell>
                          {item.itemType === 'workshop'
                            ? item.workshopRating
                              ? item.workshopRating.status === 'absent'
                                ? 'Absent'
                                : `${item.workshopRating.rating ?? '—'}/10`
                              : '—'
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          {item.itemType === 'workshop' && item.workshopRating?.comment
                            ? item.workshopRating.comment
                            : item.itemType === 'workshop'
                              ? '—'
                              : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle1" fontWeight={600} mb={1}>Quiz: score et réponses</Typography>
            {chapterData.items.filter((x) => x.itemType === 'quiz').length === 0 ? (
              <Typography variant="body2" color="text.secondary" mb={2}>Aucun quiz dans cette section.</Typography>
            ) : (
              chapterData.items
                .filter((x) => x.itemType === 'quiz')
                .map((quizItem) => (
                  <Card key={quizItem.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2">{getLocalizedText(quizItem.title)}</Typography>
                        <Chip
                          size="small"
                          color={quizItem.response ? 'primary' : 'default'}
                          label={quizItem.response ? `${quizItem.response.score}%` : 'Pas de réponse'}
                        />
                      </Box>
                      {quizItem.response?.answers?.length ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Question</TableCell>
                              <TableCell>Réponse apprenant</TableCell>
                              <TableCell>Bonne réponse</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {quizItem.response.answers.map((a) => (
                              <TableRow key={`${quizItem.id}-${a.questionId || a.questionOrder}`}>
                                <TableCell>{getLocalizedText(a.questionText, `Question ${a.questionOrder}`)}</TableCell>
                                <TableCell>{getLocalizedText(a.selectedAnswerText, '-')}</TableCell>
                                <TableCell>{getLocalizedText(a.correctAnswerText, '-')}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Aucune réponse enregistrée.</Typography>
                      )}
                    </CardContent>
                  </Card>
                ))
            )}

            <Typography variant="subtitle1" fontWeight={600} mb={1}>Capsules: réponses aux inputs</Typography>
            {chapterData.items.filter((x) => x.itemType === 'capsule').length === 0 ? (
              <Typography variant="body2" color="text.secondary">Aucune capsule dans cette section.</Typography>
            ) : (
              chapterData.items
                .filter((x) => x.itemType === 'capsule')
                .map((capsule) => (
                  <Card key={capsule.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" mb={1}>{getLocalizedText(capsule.title)}</Typography>

                      {capsule.note?.content && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Qu&apos;est-ce que tu retiens ?
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {capsule.note.content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Dernière mise à jour : {formatDate(capsule.note.updatedAt)}
                          </Typography>
                        </Box>
                      )}

                      {(capsule.modules || []).filter((m) => m.type === 'question').length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Aucun module input/question.</Typography>
                      ) : (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Module</TableCell>
                              <TableCell>Question</TableCell>
                              <TableCell>Réponse apprenant</TableCell>
                              {/* Notation : masquée tant que la correction des réponses libres n'est pas disponible
                              <TableCell>Notation</TableCell>
                              */}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(capsule.modules || [])
                              .filter((m) => m.type === 'question')
                              .map((m) => (
                                <TableRow key={m.id}>
                                  <TableCell>#{m.order}</TableCell>
                                  <TableCell>
                                    {typeof m.content === 'object'
                                      ? getLocalizedText(m.content?.question || m.content?.title || m.content?.text, '-')
                                      : String(m.content || '-')}
                                  </TableCell>
                                  <TableCell>
                                    {m.submission?.answer || <Typography variant="caption" color="text.secondary">Aucune réponse</Typography>}
                                  </TableCell>
                                  {/* Notation : masquée tant que la correction des réponses libres n'est pas disponible
                                  <TableCell>
                                    {m.submission?.isGraded ? (
                                      <Tooltip title={m.submission.feedback || ''}>
                                        <Chip size="small" color="success" label={`${m.submission.grade ?? '-'}%`} />
                                      </Tooltip>
                                    ) : (
                                      <Chip size="small" label="Non noté" variant="outlined" />
                                    )}
                                  </TableCell>
                                  */}
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                ))
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default StudentDetail;
